import {
  AccountStatementResponse,
  AddEWalletPayload,
  AddBankPayload,
  BankerApiResponse,
  BankAccountsResponse,
  BankerDashboardResponse,
  BankerRequestResponse,
  ConversionRateResponse,
  CryptoAccountsResponse,
  CryptoAddPayload,
  EWalletAccountsResponse,
  TelegramApproversResponse,
  UpdateRequestPayload,
  UpiAccountsResponse,
  UpiAddPayload,
} from '@/types';
import { apiSlice } from './apiSlice';

export interface DepositWithdrawCounts {
  pendingDepositCount: number;
  pendingWithdrawCount: number;
  totalPendingCount: number;
}

export const bankerApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    //All Bankers list
    getBankers: builder.query<
      BankerApiResponse,
      { search?: string; fromDate?: string; toDate?: string; page?: number; limit?: number }
    >({
      query: ({ search, fromDate, toDate, page, limit } = {}) => ({
        url: '/banker/admin',
        method: 'GET',
        params: {
          ...(search ? { search } : {}),
          ...(fromDate ? { fromDate } : {}),
          ...(toDate ? { toDate } : {}),
          ...(page ? { page } : {}),
          ...(limit ? { limit } : {}),
          ts: Date.now(),
        },
      }),
      providesTags: ['Bankers'],
    }),
    //Bankers Status
    updateBankerStatus: builder.mutation<{ message: string }, { id: string | number }>({
      query: ({ id }) => ({
        url: `/banker/active-banker/${id}`,
        method: 'PATCH',
      }),
      invalidatesTags: ['Bankers'],
    }),

    //All Deposit withdraw request
    getDWRequest: builder.query<
      BankerRequestResponse,
      {
        search?: string;
        fromDate?: string;
        toDate?: string;
        page?: number;
        limit?: number;
        isUpi?: boolean;
        isCrypto?: boolean;
        isWallet?: boolean;
        isBank?: boolean;
        type?: string;
        status?: string;
        isAdmin?: boolean;
        bankerId?: string | undefined;
        paymentMode?: string | null;
      }
    >({
      query: ({
        search,
        fromDate,
        toDate,
        page,
        limit,
        isUpi,
        isCrypto,
        type,
        isBank,
        isWallet,
        status,
        isAdmin,
        bankerId,
        paymentMode,
      } = {}) => ({
        url: '/banker/all-deposit-withdraw',
        method: 'GET',
        params: {
          type,
          ...(search ? { search } : {}),
          ...(fromDate ? { fromDate } : {}),
          ...(toDate ? { toDate } : {}),
          ...(page ? { page } : {}),
          ...(limit ? { limit } : {}),
          ...(isUpi ? { isUpi } : {}),
          ...(isCrypto ? { isCrypto } : {}),
          ...(isBank ? { isBank } : {}),
          ...(isWallet ? { isWallet } : {}),
          ...(status ? { status } : {}),
          ...(isAdmin ? { isAdmin } : {}),
          ...(bankerId ? { bankerId } : {}),
          ...(paymentMode ? { paymentMode } : {}),
          ts: Date.now(),
        },
      }),
      providesTags: ['Requests'],
    }),
    getDepositWithdrawCounts: builder.query<DepositWithdrawCounts, void>({
      query: () => ({
        url: '/banker/deposit-withdraw/counts',
        method: 'GET',
        params: { ts: Date.now() },
      }),
    }),
    //handle Request
    updateRequestStatus: builder.mutation<void, UpdateRequestPayload>({
      query: ({ id, status, remark, transactionCode }) => ({
        url: `/banker/${id}`,
        method: 'PATCH',
        body: { status, remark, transactionCode },
      }),
      invalidatesTags: ['Requests', 'Wallet'],
    }),

    //Get All Accounts List
    getUpiAccount: builder.query<UpiAccountsResponse, { page: number; limit: number }>({
      query: ({ page, limit }) => ({
        url: '/upi',
        method: 'GET',
        params: { page, limit, ts: Date.now() },
      }),
      providesTags: ['Upi'],
    }),
    getBankAccounts: builder.query<BankAccountsResponse, { page: number; limit: number }>({
      query: ({ page, limit }) => ({
        url: '/bank',
        method: 'GET',
        params: { page, limit, ts: Date.now() },
      }),
      providesTags: ['Account'],
    }),
    getCryptoAccounts: builder.query<CryptoAccountsResponse, void>({
      query: () => ({
        url: '/crypto',
        method: 'GET',
        params: { ts: Date.now() },
      }),
      providesTags: ['Crypto'],
    }),
    getCryptoNetwork: builder.query<any, void>({
      query: () => ({
        url: '/crypto/networks',
        method: 'GET',
        params: { ts: Date.now() },
      }),
      providesTags: ['Network'],
    }),

    //Manage Add  Account
    upiAdd: builder.mutation<void, UpiAddPayload>({
      query: (body) => ({
        url: `/upi`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Upi'],
    }),
    bankAdd: builder.mutation<void, AddBankPayload>({
      query: (body) => ({
        url: `/bank`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Account'],
    }),
    cryptoAdd: builder.mutation<void, CryptoAddPayload>({
      query: (body) => ({
        url: '/crypto',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Crypto'],
    }),

    //Delete Account
    deleteBank: builder.mutation<void, string>({
      query: (id) => ({
        url: `/bank/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Account'],
    }),
    deleteUpi: builder.mutation<void, string>({
      query: (id) => ({
        url: `/upi/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Upi'],
    }),
    deleteCrypto: builder.mutation<void, string>({
      query: (id) => ({
        url: `/crypto/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Crypto'],
    }),
    //Banker Delete
    deleteBanker: builder.mutation<void, string>({
      query: (id) => ({
        url: `/banker/bankers/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Bankers'],
    }),

    // Admins allowed to approve deposit/withdraw requests over Telegram.
    getTelegramApprovers: builder.query<TelegramApproversResponse, void>({
      query: () => ({
        url: '/banker/telegram-approvers',
        method: 'GET',
        params: { ts: Date.now() },
      }),
      providesTags: ['TelegramApprovers'],
    }),
    addTelegramApprover: builder.mutation<
      void,
      { adminEmail: string; telegramUsername: string }
    >({
      query: (body) => ({ url: '/banker/telegram-approvers', method: 'POST', body }),
      invalidatesTags: ['TelegramApprovers'],
    }),
    toggleTelegramApprover: builder.mutation<void, string>({
      query: (id) => ({ url: `/banker/telegram-approvers/${id}/toggle`, method: 'PATCH' }),
      invalidatesTags: ['TelegramApprovers'],
    }),
    unlinkTelegramApprover: builder.mutation<void, string>({
      query: (id) => ({ url: `/banker/telegram-approvers/${id}/unlink`, method: 'PATCH' }),
      invalidatesTags: ['TelegramApprovers'],
    }),
    deleteTelegramApprover: builder.mutation<void, string>({
      query: (id) => ({ url: `/banker/telegram-approvers/${id}`, method: 'DELETE' }),
      invalidatesTags: ['TelegramApprovers'],
    }),

    // eWallets (Nagad/bKash/Rocket) — several can be active at once per
    // category; the user deposit screen picks one of them at random.
    getEWallets: builder.query<EWalletAccountsResponse, { page: number; limit: number }>({
      query: ({ page, limit }) => ({
        url: '/payment',
        method: 'GET',
        params: { page, limit, ts: Date.now() },
      }),
      providesTags: ['EWallet'],
    }),
    addEWallet: builder.mutation<void, AddEWalletPayload>({
      query: (body) => ({ url: '/payment/ewallet', method: 'POST', body }),
      invalidatesTags: ['EWallet'],
    }),
    activeInactiveEWallet: builder.mutation<void, string>({
      query: (id) => ({ url: `/payment/${id}`, method: 'PATCH' }),
      invalidatesTags: ['EWallet'],
    }),
    deleteEWallet: builder.mutation<void, string>({
      query: (id) => ({ url: `/payment/${id}`, method: 'DELETE' }),
      invalidatesTags: ['EWallet'],
    }),

    //Accout Active-inactive
    activeInactiveUpi: builder.mutation<void, string>({
      query: (id) => ({
        url: `/upi/activate/${id}`,
        method: 'PATCH',
      }),
      invalidatesTags: ['Upi'],
    }),
    activeInactiveBank: builder.mutation<void, string>({
      query: (id) => ({
        url: `/bank/activate/${id}`,
        method: 'PATCH',
      }),
      invalidatesTags: ['Account'],
    }),
    activeInactiveCrypto: builder.mutation<void, string>({
      query: (id) => ({
        url: `/crypto/${id}`,
        method: 'PATCH',
      }),
      invalidatesTags: ['Crypto'],
    }),

    //Dashboard
    bankerDashboard: builder.query<
      BankerDashboardResponse,
      { fromDate?: string; toDate?: string; id?: string }
    >({
      query: ({ fromDate, toDate, id }) => {
        const url = id ? `/banker/dashboard/${id}` : `/banker/dashboard`;
        return {
          url,
          method: 'GET',
          params: {
            ...(fromDate && { fromDate }),
            ...(toDate && { toDate }),
            ts: Date.now(),
          },
        };
      },
    }),
    AllbankerDashboard: builder.query<
      BankerDashboardResponse,
      { fromDate?: string; toDate?: string; id?: string }
    >({
      query: ({ fromDate, toDate }) => {
        const url = `/banker/dashboard-admin`;
        return {
          url,
          method: 'GET',
          params: {
            ...(fromDate && { fromDate }),
            ...(toDate && { toDate }),
            ts: Date.now(),
          },
        };
      },
    }),

    //account statement
    getBankerAccountStatement: builder.query<
      AccountStatementResponse,
      {
        page?: number;
        limit?: number;
        search?: string;
        context?: string;
        fromDate?: string;
        toDate?: string;
      }
    >({
      query: ({ search, fromDate, toDate, page, limit, context }) => ({
        url: '/users/me/transactions',
        method: 'GET',
        params: {
          ...(search ? { search } : {}),
          ...(fromDate ? { fromDate } : {}),
          ...(toDate ? { toDate } : {}),
          ...(page ? { page } : {}),
          ...(context ? { context } : {}),
          ...(limit ? { limit } : {}),
          ts: Date.now(),
        },
      }),
    }),
    updateCryptoConversionRateSettings: builder.mutation<
      any,
      { cryptoId: number; conversionRate: string }
    >({
      query: ({ cryptoId, conversionRate }) => ({
        url: `/banker/conversion-rate/${cryptoId}`,
        method: 'PATCH',
        body: { conversionRate },
      }),
      invalidatesTags: ['Network'],
    }),
  }),
});

export const {
  useGetBankersQuery,
  useGetTelegramApproversQuery,
  useAddTelegramApproverMutation,
  useToggleTelegramApproverMutation,
  useUnlinkTelegramApproverMutation,
  useDeleteTelegramApproverMutation,
  useGetEWalletsQuery,
  useAddEWalletMutation,
  useActiveInactiveEWalletMutation,
  useDeleteEWalletMutation,
  useUpdateBankerStatusMutation,
  useGetDWRequestQuery,
  useGetDepositWithdrawCountsQuery,
  useUpdateRequestStatusMutation,
  useUpiAddMutation,
  useGetUpiAccountQuery,
  useBankAddMutation,
  useGetBankAccountsQuery,
  useGetCryptoAccountsQuery,
  useGetCryptoNetworkQuery,
  useCryptoAddMutation,
  useDeleteBankMutation,
  useDeleteUpiMutation,
  useDeleteCryptoMutation,
  useActiveInactiveBankMutation,
  useActiveInactiveUpiMutation,
  useActiveInactiveCryptoMutation,
  useDeleteBankerMutation,
  useBankerDashboardQuery,
  useGetBankerAccountStatementQuery,
  useAllbankerDashboardQuery,
  useUpdateCryptoConversionRateSettingsMutation,
} = bankerApi;
